import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./SupportPage.css";

const TOPICS = [
  "Portfolio Advisory",
  "Mutual Fund Queries",
  "Account & KYC",
  "Fees & Billing",
  "Technical Support",
  "Grievance / Complaint",
  "General Enquiry",
];

const CONTACT_INFO = [
  {
    icon: "fa-phone",
    label: "Phone",
    value: "+91 93145 07135",
    href: "tel:+919314507135",
    color: "#2563eb",
  },
  {
    icon: "fa-envelope",
    label: "Email",
    value: "support@wealthcompounders.in",
    href: "mailto:support@wealthcompounders.in",
    color: "#10b981",
  },
  {
    icon: "fa-clock",
    label: "Business Hours",
    value: "Mon – Sat, 9 AM – 6 PM IST",
    href: null,
    color: "#f59e0b",
  },
];

const FAQ_ITEMS = [
  {
    q: "How quickly will I get a response?",
    a: "We aim to respond to all queries within 24 business hours. Urgent matters are prioritised.",
  },
  {
    q: "Can I schedule a free consultation?",
    a: "Yes — use the Book a Consultation link in the navigation, or mention it in your message and we'll reach out to schedule one.",
  },
  {
    q: "How do I raise a complaint?",
    a: "Select 'Grievance / Complaint' as the subject and describe your concern. You can also escalate to SCORES (SEBI Complaints Redress System) if needed.",
  },
  {
    q: "Is my information secure?",
    a: "Absolutely. All data shared is encrypted and kept strictly confidential, in line with applicable data protection laws. We never sell personal data.",
  },
];

export default function SupportPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [focused, setFocused] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="wsp-wrapper">
      <Navbar />

      {/*HERO */}
      <section className="wsp-hero">
        <div className="wsp-hero-bg" />
        <div className="wsp-hero-dotgrid" />
        <div className="wsp-glow wsp-glow-l" />
        <div className="wsp-glow wsp-glow-r" />

        <div className="wsp-hero-inner">
          <div className="wsp-eyebrow">
            <span className="wsp-eyebrow-dot" />
            Get in Touch
          </div>
          <h1 className="wsp-hero-title">
            We're Here <span className="wsp-gradient-text">to Help</span>
          </h1>
          <p className="wsp-hero-sub">
            Have a question about your investments or our services? We're here
            to help. Reach out and our team will get back to you within 24
            hours.
          </p>
        </div>
      </section>

      {/* CONTACT FORM SECTION */}
      <section className="wsp-contact-section">
        <div className="wsp-container">
          <div className="wsp-contact-grid">
            {/* ── LEFT: Contact info card ── */}
            <div className="wsp-info-card">
              <div className="wsp-info-card-bg" />
              <div className="wsp-info-orb wsp-orb-1" />
              <div className="wsp-info-orb wsp-orb-2" />

              <div className="wsp-info-content">
                <h2 className="wsp-info-title">Contact Information</h2>
                <p className="wsp-info-sub">
                  We'd love to hear from you. Drop us a line anytime.
                </p>

                <div className="wsp-contact-list">
                  {CONTACT_INFO.map((c) => (
                    <div key={c.label} className="wsp-contact-item">
                      <div className="wsp-contact-item-icon">
                        <i className={`fas ${c.icon}`} />
                      </div>
                      <div>
                        <span className="wsp-contact-item-label">
                          {c.label}
                        </span>
                        {c.href ? (
                          <a href={c.href} className="wsp-contact-item-val">
                            {c.value}
                          </a>
                        ) : (
                          <span className="wsp-contact-item-val">
                            {c.value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="wsp-info-secure">
                  <i className="fas fa-shield-halved" />
                  <span>
                    Your information is secure and will never be shared with
                    third parties.
                  </span>
                </div>
              </div>
            </div>

            {/* RIGHT: Contact form*/}
            <div className="wsp-form-card">
              {submitted ? (
                <div className="wsp-success">
                  <div className="wsp-success-icon">
                    <i className="fas fa-circle-check" />
                  </div>
                  <h3>Message Sent!</h3>
                  <p>
                    Thanks for reaching out. Our team will get back to you
                    within 24 business hours.
                  </p>
                  <button
                    className="wsp-success-btn"
                    onClick={() => setSubmitted(false)}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="wsp-form" onSubmit={handleSubmit}>
                  <div className="wsp-form-row">
                    <div
                      className={`wsp-field${focused === "name" ? " wsp-field-focused" : ""}${form.name ? " wsp-field-filled" : ""}`}
                    >
                      <label className="wsp-label">
                        Full Name <span className="wsp-required">*</span>
                      </label>
                      <div className="wsp-input-wrap">
                        <i className="fas fa-user wsp-input-icon" />
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Your full name"
                          value={form.name}
                          onChange={handleChange}
                          onFocus={() => setFocused("name")}
                          onBlur={() => setFocused("")}
                          className="wsp-input"
                        />
                      </div>
                    </div>

                    <div
                      className={`wsp-field${focused === "email" ? " wsp-field-focused" : ""}${form.email ? " wsp-field-filled" : ""}`}
                    >
                      <label className="wsp-label">
                        Email Address <span className="wsp-required">*</span>
                      </label>
                      <div className="wsp-input-wrap">
                        <i className="fas fa-envelope wsp-input-icon" />
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={handleChange}
                          onFocus={() => setFocused("email")}
                          onBlur={() => setFocused("")}
                          className="wsp-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="wsp-form-row">
                    <div
                      className={`wsp-field${focused === "phone" ? " wsp-field-focused" : ""}${form.phone ? " wsp-field-filled" : ""}`}
                    >
                      <label className="wsp-label">Phone Number</label>
                      <div className="wsp-input-wrap">
                        <i className="fas fa-phone wsp-input-icon" />
                        <input
                          type="tel"
                          name="phone"
                          placeholder="+91 XXXXX XXXXX"
                          value={form.phone}
                          onChange={handleChange}
                          onFocus={() => setFocused("phone")}
                          onBlur={() => setFocused("")}
                          className="wsp-input"
                        />
                      </div>
                    </div>

                    <div
                      className={`wsp-field${focused === "subject" ? " wsp-field-focused" : ""}${form.subject ? " wsp-field-filled" : ""}`}
                    >
                      <label className="wsp-label">
                        Subject <span className="wsp-required">*</span>
                      </label>
                      <div className="wsp-input-wrap wsp-select-wrap">
                        <i className="fas fa-tag wsp-input-icon" />
                        <select
                          name="subject"
                          required
                          value={form.subject}
                          onChange={handleChange}
                          onFocus={() => setFocused("subject")}
                          onBlur={() => setFocused("")}
                          className="wsp-input wsp-select"
                        >
                          <option value="">Select a topic</option>
                          {TOPICS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down wsp-select-chevron" />
                      </div>
                    </div>
                  </div>

                  <div
                    className={`wsp-field wsp-field-full${focused === "message" ? " wsp-field-focused" : ""}${form.message ? " wsp-field-filled" : ""}`}
                  >
                    <label className="wsp-label">
                      Message <span className="wsp-required">*</span>
                    </label>
                    <div className="wsp-input-wrap wsp-textarea-wrap">
                      <i className="fas fa-comment-dots wsp-input-icon wsp-textarea-icon" />
                      <textarea
                        name="message"
                        required
                        rows={5}
                        placeholder="Tell us how we can help you..."
                        value={form.message}
                        onChange={handleChange}
                        onFocus={() => setFocused("message")}
                        onBlur={() => setFocused("")}
                        className="wsp-input wsp-textarea"
                      />
                    </div>
                  </div>

                  <button type="submit" className="wsp-submit-btn">
                    Send Message <i className="fas fa-paper-plane" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="wsp-faq-section">
        <div className="wsp-faq-bg" />
        <div className="wsp-container">
          <div className="wsp-faq-header">
            <div className="wsp-pill">
              <i className="fas fa-circle-question" /> FAQ
            </div>
            <h2 className="wsp-faq-title">
              Frequently Asked{" "}
              <span className="wsp-gradient-text">Questions</span>
            </h2>
            <p className="wsp-faq-sub">
              Quick answers to the most common support queries.
            </p>
          </div>

          <div className="wsp-faq-grid">
            {FAQ_ITEMS.map((item, i) => (
              <div
                key={i}
                className={`wsp-faq-card${openFaq === i ? " wsp-faq-open" : ""}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="wsp-faq-q">
                  <span>{item.q}</span>
                  <i className="fas fa-chevron-down wsp-faq-chevron" />
                </div>
                <div className="wsp-faq-body">
                  <div className="wsp-faq-body-overflow">
                    <p className="wsp-faq-a">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
